import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/joy';
import {
  CaretLeft as BackIcon,
  Desktop as DesktopIcon,
  DeviceMobile as MobileIcon,
  DeviceTablet as TabletIcon,
  ArrowSquareOut as OpenInNewIcon,
} from '@phosphor-icons/react';
import api from '../../../lib/api';

export const WebsitePreview: React.FC = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  const { data: project } = useQuery({
    queryKey: ['website-builder-project', projectId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getProject(projectId!);
      return response.data;
    },
    enabled: !!projectId,
  });

  const previewUrl = project?.preview_url || project?.production_url;
  const widthMap = { desktop: '100%', tablet: '768px', mobile: '375px' };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="plain"
            size="sm"
            startDecorator={<BackIcon />}
            onClick={() => navigate(`/marketing/website-builder/${projectId}`)}
          >
            Back to Editor
          </Button>
          <Typography level="title-sm">{project?.name}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              variant={device === 'desktop' ? 'solid' : 'outlined'}
              size="sm"
              onClick={() => setDevice('desktop')}
            >
              <DesktopIcon />
            </IconButton>
            <IconButton
              variant={device === 'tablet' ? 'solid' : 'outlined'}
              size="sm"
              onClick={() => setDevice('tablet')}
            >
              <TabletIcon />
            </IconButton>
            <IconButton
              variant={device === 'mobile' ? 'solid' : 'outlined'}
              size="sm"
              onClick={() => setDevice('mobile')}
            >
              <MobileIcon />
            </IconButton>
          </Box>

          {previewUrl && (
            <Button
              size="sm"
              variant="outlined"
              color="primary"
              endDecorator={<OpenInNewIcon />}
              onClick={() => window.open(previewUrl, '_blank')}
            >
              Open in New Tab
            </Button>
          )}
        </Box>
      </Box>

      {/* Preview */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          bgcolor: 'neutral.100',
          overflow: 'auto',
          p: device !== 'desktop' ? 2 : 0,
        }}
      >
        <Box
          sx={{
            width: widthMap[device],
            maxWidth: '100%',
            height: '100%',
            bgcolor: 'white',
            boxShadow: device !== 'desktop' ? 'lg' : 'none',
            borderRadius: device !== 'desktop' ? 'lg' : 0,
            overflow: 'hidden',
          }}
        >
          {previewUrl ? (
            <iframe
              src={previewUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Website Preview"
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                No preview available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
